import type { BrowserProductQueryQuery } from '@generated/graphql'

import { request } from '../graphql/request'
import type { CartItem } from './validate'

export type AddItemsInput = { sku: string; qty: number }

export type AddItemsFailureReason = 'unavailable' | 'max_qty' | 'error'

export type AddItemsResult = {
  added: Array<{ sku: string; qty: number }>
  failed: Array<{ sku: string; reason: AddItemsFailureReason }>
}

export const ADD_ITEMS_TIMEOUT_MS = 10000
export const CART_UPDATED_EVENT = 'arcaplanet:cart:updated'

type SkuProduct = NonNullable<BrowserProductQueryQuery['product']>

export const fetchSkuProduct = async (
  sku: string,
  channel: string,
  locale: string
): Promise<SkuProduct | null> => {
  const data = await request<BrowserProductQueryQuery>('BrowserProductQuery', {
    locator: [
      { key: 'id', value: sku },
      { key: 'channel', value: channel },
      { key: 'locale', value: locale },
    ],
  })

  return data?.product ?? null
}

export type SkuCheck =
  | { ok: true }
  | { ok: false; reason: Exclude<AddItemsFailureReason, 'error'> }

// Stesso criterio di disponibilita' usato dalla PDP (pages/[slug]/p.tsx):
// availableQuantity e' un ordine di grandezza, non lo stock esatto.
export const checkSkuAvailability = (
  product: SkuProduct | null,
  qty: number
): SkuCheck => {
  const offer = product?.offers?.offers?.[0]

  if (!product || !offer) {
    return { ok: false, reason: 'unavailable' }
  }

  const availableQuantity = product.availableProducts?.availableQuantity

  const isAvailable =
    availableQuantity != null
      ? Number(availableQuantity) > 0
      : offer.availability === 'https://schema.org/InStock'

  if (!isAvailable) {
    return { ok: false, reason: 'unavailable' }
  }

  if (availableQuantity != null && qty > Number(availableQuantity)) {
    return { ok: false, reason: 'max_qty' }
  }

  return { ok: true }
}

export const buildCartItemFromProduct = (
  product: SkuProduct,
  qty: number,
  tokens: {
    userToken?: string | null
    authenticatedUserToken?: string | null
    bk_account_id?: string
    vtex_user_id?: string
    emailHash?: string | null
  } = {}
): CartItem => {
  const offer = product.offers.offers[0]

  return {
    id: product.id,
    price: offer.price,
    listPrice: offer.listPrice,
    seller: offer.seller,
    quantity: qty,
    isKit: product.stockKeepingUnit?.isKit,
    itemOffered: {
      userToken: tokens.userToken,
      authenticatedUserToken: tokens.authenticatedUserToken || undefined,
      bk_account_id: tokens.bk_account_id,
      vtex_user_id: tokens.vtex_user_id,
      emailHash: tokens.emailHash,
      sku: product.sku,
      name: product.name,
      breadcrumbList: product.breadcrumbList,
      gtin: product.gtin,
      image: product.image,
      brand: product.brand,
      isVariantOf: product.isVariantOf,
      additionalProperty: product.additionalProperty,
      productSpecifications: product.productSpecifications,
      stockKeepingUnit: product.stockKeepingUnit,
      structuredData: {
        minPriceThirtyDays: product.structuredData?.minPriceThirtyDays || '',
      },
    },
  } as CartItem
}

export const sanitizeAddItemsInput = (items: unknown): AddItemsInput[] => {
  if (!Array.isArray(items) || items.length === 0) {
    throw new TypeError('addItems expects a non-empty array of { sku, qty }')
  }

  const merged = new Map<string, number>()

  for (const entry of items) {
    const sku = String(entry?.sku ?? '').trim()
    const qty = Number(entry?.qty ?? 1)

    if (!sku || !Number.isInteger(qty) || qty < 1) {
      throw new TypeError(`addItems: invalid item ${JSON.stringify(entry)}`)
    }

    merged.set(sku, (merged.get(sku) ?? 0) + qty)
  }

  return Array.from(merged, ([sku, qty]) => ({ sku, qty }))
}
